import { last, uniq } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useDirectoryFilter } from '../../../../data/hooks/directory/useDirectoryFilter';
import { useDirectorySubItems } from '../../../../data/hooks/directory/useDirectorySubItems/useDirectorySubItems';
import { useDirectoryTagCategories } from '../../../../data/hooks/directory/useDirectoryTagCategories';
import { useEvent } from '../../../../data/hooks/eventHooks';
import { useI18n } from '../../../../data/hooks/i18nHooks';
import ButtonLink from '../../../elements/ButtonLink/ButtonLink';
import Scrollable from '../../../elements/Scrollable';
import TopBarEvent from '../../../elements/TopBar/TopBarEvent';
import LoadDirectoryItem from '../../../loaders/LoadDirectoryItem';
import WaitWhileBusy from '../../../WaitWhileBusy';
import Screen from '../../Screen/Screen';
import DirectoryFilterTagCategory from './DirectoryFilterTagCategory';
import { createDirectoryFilterFromFilterParams } from '../../../../data/filters/directory/createDirectoryFilterFromFilterParams/createDirectoryFilterFromFilterParams';

function EventDirectoryFilterScreen(props) {
    const { eventId, itemId } = props,
        event = useEvent(),
        i18n = useI18n(),
        categories = useDirectoryTagCategories(eventId, itemId),
        filterParams = useDirectoryFilter(eventId, itemId),
        filtered = useDirectorySubItems(eventId, itemId, {
            filter: createDirectoryFilterFromFilterParams(filterParams),
        });

    // if there are no categories, close the filter screen
    useEffect(() => {
        if (!categories.length) {
            app.history.replace('..');
        }
    }, [categories]);

    const applyFilter = useCallback(() => {
        // no need to apply, since the filter it's already set on the store
        app.history.push('..');
    }, []);

    const handleSelectionChange = useCallback(
        (cat, option, checked) => {
            let nextFilterParams;
            if (checked) {
                nextFilterParams = {
                    ...filterParams,
                    tags: {
                        ...(filterParams.tags ?? {}),
                        [cat.name]: uniq([...(filterParams.tags?.[cat.name] ?? []), option.name]),
                    },
                };
            } else {
                nextFilterParams = {
                    ...filterParams,
                    tags: {
                        ...(filterParams.tags ?? {}),
                        [cat.name]: filterParams.tags?.[cat.name].filter((opt) => opt !== option.name),
                    },
                };
            }
            app.store.dispatch(app.actions.directoryFilter.setDirectoryFilter(eventId, itemId, nextFilterParams));
        },
        [eventId, itemId, filterParams]
    );

    const header = (
            <>
                <TopBarEvent event={event} returnURL="..">
                    {i18n['label.filter']}
                </TopBarEvent>
                <hr />
            </>
        ),
        footer = (
            <>
                <hr />
                <div className="content">
                    <ButtonLink className="primary" onClick={applyFilter}>
                        {i18n['button.view.results']}
                        <span className="marginLeft">
                            {' '}
                            <strong>({filtered.length})</strong>
                        </span>
                    </ButtonLink>
                </div>
            </>
        );

    return (
        <Screen header={header} footer={footer}>
            <Scrollable>
                {categories.map((category, index) => (
                    <DirectoryFilterTagCategory key={index} category={category} filterParams={filterParams} onSelectionChange={handleSelectionChange} defaultExpanded={index == 0} />
                ))}
            </Scrollable>
        </Screen>
    );
}

export default (props) => {
    const { eventId, path } = props.match.params,
        itemId = last(path.split('/'));

    return (
        <LoadDirectoryItem eventId={eventId} items={[itemId]} key={itemId}>
            <WaitWhileBusy>
                <EventDirectoryFilterScreen eventId={eventId} itemId={itemId} />
            </WaitWhileBusy>
        </LoadDirectoryItem>
    );
};
